import {
  Box,
  Button,
  Card,
  CardBody,
  DataList,
  DrawerBody,
  Icon,
  IconButton,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { type Updater, useForm, useStore } from '@tanstack/react-form';
import type { UseMutationResult } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { type Dispatch, type SetStateAction, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSave, FaTimes } from 'react-icons/fa';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { MdOutlineInfo } from 'react-icons/md';

import type {
  GlobalReferentialIndicatorUpdate,
  IndicatorDetailsPublic,
  IndicatorUpdate,
  ReferentialKeyPublic,
} from '@/client';
import { CompositeIndicatorInput, IndicatorInput, SimpleIndicatorInput } from '@/components/common/IndicatorInput';
import { QuillInput } from '@/components/common/QuillInput';
import { SelectCommitment } from '@/components/common/SelectCommitment';
import SelectReferentials from '@/components/common/SelectReferentials';
import SelectReferentialsKeys from '@/components/common/SelectReferentialsKeys';
import SelectSdgs from '@/components/common/SelectSdgs';
import SelectUsers from '@/components/common/SelectUsers';
import { Tooltip } from '@/components/ui/tooltip';
import useCustomToast from '@/hooks/use-custom-toast';

import { DrawerItem } from './DrawerItem';

type IndicatorDrawerProps = {
  isOpen: boolean;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  indicatorData?: IndicatorDetailsPublic | undefined;
  mutation: UseMutationResult<IndicatorDetailsPublic, Error, IndicatorUpdate, unknown>;
};

const IndicatorDrawer = ({ isOpen, setEditMode, indicatorData, mutation }: IndicatorDrawerProps) => {
  const { slug } = useParams({ strict: false });
  const { t } = useTranslation();
  const showToast = useCustomToast();

  const form = useForm({
    defaultValues: {
      name: indicatorData?.name ?? '',
      unit: indicatorData?.unit ?? '',
      category: indicatorData?.category ?? 'simple',
      numerator: indicatorData?.numerator ?? '',
      denominator_id: indicatorData?.denominator_id ?? null,
      commitment_id: indicatorData?.commitment_id ?? null,
      description: indicatorData?.description ?? '',
      department: indicatorData?.department ?? '',
      responsibles: indicatorData?.responsibles ?? [],
      sdgs: indicatorData?.sdgs ?? [],
      referentials: indicatorData?.referentials.map(
        (ref) =>
          ({
            id: ref.id ?? null,
            global_referential_id: ref.global_referential?.id ?? null,
            global_referential_category_id: ref.global_referential_category?.id ?? null,
            global_referential_sub_category_id: ref.global_referential_sub_category?.id ?? null,
          }) as GlobalReferentialIndicatorUpdate
      ) ?? [
        {
          id: null,
          global_referential_id: null,
          global_referential_category_id: null,
          global_referential_sub_category_id: null,
        },
      ],
      referentialsKeys: indicatorData?.referentials_keys ?? [],
    },
    onSubmit: (values) => {
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      const payload: Record<string, any> = {};

      if (values.value.name !== indicatorData?.name) {
        payload.name = values.value.name ?? null;
      }
      if (values.value.unit !== indicatorData?.unit) {
        payload.unit = values.value.unit ?? null;
      }
      if (values.value.commitment_id !== indicatorData?.commitment_id) {
        payload.commitment_id = values.value.commitment_id ?? null;
      }
      if (values.value.numerator !== indicatorData?.numerator) {
        payload.numerator = values.value.numerator ?? null;
      }
      if (values.value.denominator_id !== indicatorData?.denominator_id) {
        payload.denominator_id = values.value.denominator_id ?? null;
      }
      if (values.value.category !== indicatorData?.category) {
        payload.category = values.value.category ?? null;
      }
      if (values.value.description !== indicatorData?.description) {
        payload.description = values.value.description ?? null;
      }
      if (values.value.department !== indicatorData?.department) {
        payload.department = values.value.department ?? null;
      }
      if (JSON.stringify(values.value.responsibles) !== JSON.stringify(indicatorData?.responsibles)) {
        payload.responsibles = values.value.responsibles.map((user) => user.id) ?? null;
      }
      if (JSON.stringify(values.value.sdgs) !== JSON.stringify(indicatorData?.sdgs)) {
        payload.sdgs = values.value.sdgs.map((sdg) => sdg.code) ?? null;
      }
      if (JSON.stringify(values.value.referentials) !== JSON.stringify(indicatorData?.referentials)) {
        payload.referentials = values.value.referentials ?? [];
      }
      if (JSON.stringify(values.value.referentialsKeys) !== JSON.stringify(indicatorData?.referentials_keys)) {
        payload.referentials_keys = values.value.referentialsKeys ?? [];
      }
      mutation.mutate(payload);

      showToast('Indicateur mis à jour', '', 'success');
    },
  });

  useEffect(() => {
    if (!isOpen) setEditMode(false);
  }, [isOpen]);

  const category = useStore(form.store, (state) => state.values.category);

  return (
    <DrawerBody pb={20}>
      {indicatorData ? (
        <DataList.Root
          orientation="horizontal"
          divideY="1px"
          mb={4}
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <DrawerItem title={t('indicator.columnsHeader.indicatorName')}>
            <form.Field name="name">
              {(field) => (
                <Input
                  id="name"
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              )}
            </form.Field>
          </DrawerItem>
          <DrawerItem title={t('indicator.columnsHeader.unit')}>
            <form.Field name="unit">
              {(field) => (
                <Input
                  id="unit"
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              )}
            </form.Field>
          </DrawerItem>
          <DrawerItem
            title={
              <Text lineHeight={4}>
                {t('indicator.columnsHeader.pillarName')}
                <br />
                {t('indicator.columnsHeader.commitmentName')}
              </Text>
            }
          >
            <form.Field name="commitment_id">
              {(field) => (
                <SelectCommitment value={field.state.value} onChange={(e) => field.handleChange(e)} onLine={false} />
              )}
            </form.Field>
          </DrawerItem>
          <DrawerItem title={t('indicator.columnsHeader.description')}>
            <form.Field name="description">
              {(field) => (
                <>
                  <QuillInput
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.value ?? '')}
                    placeholder={t('indicator.columnsHeader.description.placeholder')}
                  />
                </>
              )}
            </form.Field>
          </DrawerItem>

          <DrawerItem title={t('indicator.columnsHeader.department')}>
            <form.Field name="department">
              {(field) => (
                <Input
                  id="department"
                  type="text"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              )}
            </form.Field>
          </DrawerItem>

          <DrawerItem title={t('indicator.columnsHeader.categoryName')}>
            <VStack placeItems={'start'} w={'full'}>
              <form.Field name="category">
                {(field) => (
                  <IndicatorInput value={field.state.value} onChange={(value) => field.handleChange(value)} />
                )}
              </form.Field>
              {category === 'composite' && (
                <>
                  <Text fontWeight={'semibold'}>
                    Numerateur
                    <Tooltip
                      showArrow
                      content={
                        ' Tapez votre formule avec des nombres, symboles mathématiques et des indicateurs. Utilisez @ pour rechercher un indicateur.'
                      }
                      openDelay={100}
                      closeDelay={100}
                      contentProps={{
                        css: { '--tooltip-bg': 'colors.blue.100', '--r3-colors-fg-inverted': 'colors.blue.700' },
                      }}
                    >
                      <Icon ml={1} color={'blue.700'} size={'sm'} rounded={'full'} cursor={'pointer'}>
                        <MdOutlineInfo />
                      </Icon>
                    </Tooltip>
                  </Text>
                  <form.Field name="numerator">
                    {(field) => (
                      <CompositeIndicatorInput
                        value={field.state.value}
                        onChange={(value) => field.handleChange(value)}
                      ></CompositeIndicatorInput>
                    )}
                  </form.Field>
                  <Text fontWeight={'semibold'}>Dénomiateur</Text>
                  <form.Field name="denominator_id">
                    {(field) => (
                      <SimpleIndicatorInput
                        value={field.state.value}
                        onChange={(value) => field.handleChange(value)}
                      ></SimpleIndicatorInput>
                    )}
                  </form.Field>
                </>
              )}
            </VStack>
          </DrawerItem>

          {indicatorData?.category === 'composite' && (
            <>
              <DrawerItem title={t('indicator.columnsHeader.formula')}>
                {indicatorData?.formula ?? (
                  <Text color="gray.500" textStyle="sm">
                    N/R
                  </Text>
                )}
              </DrawerItem>
              <DrawerItem title={t('indicator.columnsHeader.formulaUnits')}>
                {indicatorData?.formula_units ?? (
                  <Text color="gray.500" textStyle="sm">
                    N/R
                  </Text>
                )}
              </DrawerItem>
            </>
          )}
          <DrawerItem title={t('indicator.columnsHeader.responsibles')}>
            <form.Field name="responsibles">
              {(field) => (
                <SelectUsers
                  slug={slug}
                  value={field.state.value!.map((user) => user.id.toString())}
                  onChange={(selectedUsers) => {
                    field.handleChange(selectedUsers);
                  }}
                />
              )}
            </form.Field>
          </DrawerItem>
          <DataList.Item pt="4">
            <DataList.ItemLabel>
              {t('indicator.columnsHeader.sdgs')}
              <Tooltip
                showArrow
                openDelay={100}
                closeDelay={100}
                content={t('indicator.tooltip.sdgs.edit')}
                contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
              >
                <IconButton variant="ghost" aria-label="info" size="2xs" colorPalette="gray">
                  <HiOutlineInformationCircle />
                </IconButton>
              </Tooltip>
            </DataList.ItemLabel>
            <DataList.ItemValue>
              <form.Field name="sdgs">
                {(field) => (
                  <SelectSdgs
                    value={field.state.value?.map((sdg) => sdg.code) ?? indicatorData?.sdgs.map((sdg) => sdg.code)}
                    onChange={(e) => {
                      field.handleChange(e);
                    }}
                  />
                )}
              </form.Field>
            </DataList.ItemValue>
          </DataList.Item>
          <DrawerItem title={t('indicator.columnsHeader.referentials')}>
            <form.Field name="referentials">
              {(field) => (
                <SelectReferentials
                  value={indicatorData?.referentials.map((referential) => ({
                    id: referential.id ?? null,
                    global_referential_id: referential.global_referential?.id ?? null,
                    global_referential_category_id: referential.global_referential_category?.id ?? null,
                    global_referential_sub_category_id: referential.global_referential_sub_category?.id ?? null,
                  }))}
                  onChange={(e) => {
                    field.handleChange([...e] as Updater<GlobalReferentialIndicatorUpdate[]>);
                  }}
                />
              )}
            </form.Field>
          </DrawerItem>
          <DrawerItem title={t('indicator.columnsHeader.externalReferentialsKeys')}>
            <form.Field name="referentialsKeys">
              {(field) => (
                <SelectReferentialsKeys
                  value={field.state.value ?? []}
                  onChange={(e) => {
                    field.handleChange([...e] as Updater<ReferentialKeyPublic[]>);
                  }}
                />
              )}
            </form.Field>
          </DrawerItem>
        </DataList.Root>
      ) : (
        <Card.Root>
          <CardBody>{t('indicator.noSelection')}</CardBody>
        </Card.Root>
      )}

      <Box position="fixed" bottom={10} right={10}>
        <Button variant="outline" bg={{ base: 'white', _dark: 'red' }} onClick={() => setEditMode(false)}>
          <FaTimes /> {t('ui.cancel')}
        </Button>
        <Button ml={4} type="submit" onClick={form.handleSubmit}>
          <FaSave /> {t('ui.save')}
        </Button>
      </Box>
    </DrawerBody>
  );
};

export default IndicatorDrawer;
